/**
 * League rollover merging — shared implementation for the shell.
 *
 * Sleeper creates a NEW league id each season; the new league points back via
 * previous_league_id. Users think "I'm in Crunkwrap", not in two league ids.
 * See CLAUDE.md "Handling Sleeper Leagues Between Seasons" for the full rules.
 *
 * previousLeagueId === '0' means a startup league (no previous season) — valid, not an error.
 */

/**
 * Collapse rollover chains so each real-world league appears once (its newest season),
 * with prior seasons attached as `previousSeasons` (newest first).
 *
 * @param {Array<{leagueId: string, previousLeagueId?: string|null}>} leagues
 * @returns {Array} merged leagues — same objects, superseded seasons folded in
 */
/**
 * One-line league format summary: "12 teams · SF · PPR · TEP"
 */
export function leagueMeta(league) {
  if (!league) return '';
  const parts = [`${league.numTeams} teams`, league.isSuperFlex ? 'SF' : '1QB'];
  if (league.ppr >= 1) parts.push('PPR');
  else if (league.ppr > 0) parts.push('Half PPR');
  if (league.tePremium > 0) parts.push('TEP');
  return parts.join(' · ');
}

/**
 * Season-aware team record line. Sleeper records read 0-0 all offseason, so
 * fall back to last season's record (labeled) until the current one has games.
 *
 * @param {object|string|null} record current-season record ({ wins, losses, ties? } or "7-7")
 * @param {object|string|null} prevRecord previous-season record, same shapes
 * @param {string|null} prevSeasonLabel e.g. "2025" — labels the fallback as "7-7 in 2025"
 * @returns {string|null} "7-7", "7-6-1", "7-7 in 2025", "7-7 last season", or null
 */
export const formatTeamRecord = (record, prevRecord, prevSeasonLabel) => {
  const fmt = (rec) => {
    if (!rec) return null;
    if (typeof rec === 'string') return rec.trim() || null;
    if (rec.wins == null && rec.losses == null) return null;
    const base = `${rec.wins || 0}-${rec.losses || 0}`;
    return rec.ties ? `${base}-${rec.ties}` : base;
  };
  const hasGames = (rec) => {
    if (!rec) return false;
    if (typeof rec === 'string') return /[1-9]/.test(rec);
    return ((rec.wins || 0) + (rec.losses || 0) + (rec.ties || 0)) > 0;
  };
  if (hasGames(record)) return fmt(record);
  if (hasGames(prevRecord)) {
    const prev = fmt(prevRecord);
    return prevSeasonLabel ? `${prev} in ${prevSeasonLabel}` : `${prev} last season`;
  }
  return null;
};

export function mergeLeagueRollovers(leagues) {
  if (!Array.isArray(leagues) || leagues.length === 0) return [];

  const byId = new Map(leagues.map((l) => [l.leagueId, l]));

  // A league is superseded if any other league in the set points to it as previous.
  const supersededIds = new Set();
  leagues.forEach((l) => {
    const prev = l.previousLeagueId;
    if (prev && prev !== '0' && byId.has(prev)) {
      supersededIds.add(prev);
    }
  });

  return leagues
    .filter((l) => !supersededIds.has(l.leagueId))
    .map((l) => {
      const previousSeasons = [];
      let current = l;
      // Walk the chain; guard against cycles via the visited set.
      const visited = new Set([l.leagueId]);
      while (
        current.previousLeagueId &&
        current.previousLeagueId !== '0' &&
        byId.has(current.previousLeagueId) &&
        !visited.has(current.previousLeagueId)
      ) {
        current = byId.get(current.previousLeagueId);
        visited.add(current.leagueId);
        previousSeasons.push(current);
      }
      return previousSeasons.length > 0
        ? { ...l, previousSeasons, mergedFromPrevious: true }
        : l;
    });
}
